import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Heading } from "~/components/ui/heading";
import { CDNImage } from "~/components/ui/cdn-image";
import { CCOType } from "~/lib/schemas";
import { Button } from "~/components/ui/button";

export const Route = createFileRoute("/_public/coaches/cco/")({
  head: () => ({
    title: "Our Coaching Organizations",
    meta: [
      {
        name: "description",
        content:
          "Find coaches certified by leading organizations like ICF, EMCC, and top training schools.",
      },
    ],
  }),
  loader: async ({ context }) => {
    const { getGroupedCCOs } = await import("~/lib/ccos");
    await context.queryClient.ensureQueryData({
      queryKey: ["grouped-ccos"],
      queryFn: () => getGroupedCCOs(),
    });
    return {};
  },
  component: CCOIndexPage,
});

function CCOIndexPage() {
  const [constants, setConstants] = React.useState<{
    CCO_TYPE_DISPLAY_NAME: Record<CCOType, string>;
    UNCATEGORIZED_KEY: string;
  } | null>(null);

  React.useEffect(() => {
    import("~/lib/ccos").then((mod) => {
      setConstants({
        CCO_TYPE_DISPLAY_NAME: mod.CCO_TYPE_DISPLAY_NAME,
        UNCATEGORIZED_KEY: mod.UNCATEGORIZED_KEY,
      });
    });
  }, []);

  const { data: groupedCCOs } = useQuery({
    queryKey: ["grouped-ccos"],
    queryFn: async () => {
      const { getGroupedCCOs } = await import("~/lib/ccos");
      return getGroupedCCOs();
    },
  });

  // Define the order in which to display types
  const typeOrder: CCOType[] = [CCOType.PAO, CCOType.CTO];

  if (!groupedCCOs || !constants) {
    return <div>Loading...</div>;
  }

  const { CCO_TYPE_DISPLAY_NAME, UNCATEGORIZED_KEY } = constants;

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-16 px-4 py-12 sm:px-6 lg:px-8">
      <Heading
        isH1
        preHeadline="These coaches have proven skills"
        headline="Find coaches certified by leading organizations in the coaching industry"
        subheadline="At Dream Coach Match, we understand that coaches are here to drive results. Coaches that are certified by leading organizations are more likely to deliver the results you need."
      />
      {typeOrder.map((type) => {
        const typeGroup = groupedCCOs[type];
        if (!typeGroup) {
          return null;
        } // Skip type if no CCOs

        const typeDisplayName =
          CCO_TYPE_DISPLAY_NAME[type as keyof typeof CCO_TYPE_DISPLAY_NAME] ??
          type;

        return (
          <div key={type}>
            <h2 className="font-heading mb-12 text-center text-3xl font-medium tracking-tight">
              {typeDisplayName}
            </h2>
            {Object.entries(typeGroup).map(([subcategory, ccosInGroup]) => (
              <div key={subcategory} className="mb-12">
                {/* Only show subcategory title if not 'Uncategorized' */}
                {subcategory !== UNCATEGORIZED_KEY && (
                  <h3 className="font-heading mb-8 text-center text-xl font-medium tracking-tight text-gray-600">
                    {subcategory}
                  </h3>
                )}

                {/* Apply the limit of 3 here */}
                {ccosInGroup.length > 0 ? (
                  <div className="mb-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {ccosInGroup.slice(0, 3).map((cco) => (
                      <a href={`/coaches/cco/${cco.slug}`} key={cco.id}>
                        <Card
                          key={cco.id}
                          className="flex h-full flex-col justify-between transition-transform hover:-translate-y-0.5 hover:shadow-md"
                        >
                          <CardHeader className="flex grow flex-row items-start gap-4">
                            {cco.logoUrl && (
                              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-100">
                                <CDNImage
                                  src={cco.logoUrl}
                                  alt={`${cco.name} logo`}
                                  className="h-full w-full object-contain"
                                  width={64}
                                  height={64}
                                />
                              </div>
                            )}
                            <CardTitle>{cco.name}</CardTitle>
                          </CardHeader>
                          <CardContent className="flex shrink-0 grow-0">
                            {cco.description && (
                              <p className="line-clamp-3 text-gray-600">
                                {cco.description}
                              </p>
                            )}
                          </CardContent>
                          <CardFooter>
                            <Button variant="ghost" className="ml-auto">
                              View Coaches →
                            </Button>
                          </CardFooter>
                        </Card>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-center text-gray-500">
                    No organizations found in this category.
                  </p>
                )}
              </div>
            ))}
          </div>
        );
      })}
      {/* Update total count logic and add link to full list */}
      <div className="mt-12 text-center text-gray-500">
        <p>
          Showing a selection of our partner organizations.{" "}
          <a href="/ccos" className="text-brand hover:underline">
            View all organizations
          </a>
          .
          <br className="my-1" />
          <a href="/page/coach" className="text-brand hover:underline">
            Are you a coach? Join our platform
          </a>
        </p>
      </div>
    </div>
  );
}
